/**
 * components/autotrader/AutoTraderPanel.tsx
 * Kill switch, PAPER/LIVE mode banner, circuit-breaker status, open
 * auto-positions, and the live decision log — the control surface for the
 * autonomous trading loop. Everything here is deliberately explicit: no
 * ambient "it's just on" state without the mode and status being visible
 * at all times, given the whole point of this feature is that no one is
 * watching when it acts.
 */

import { useState, useEffect, useCallback } from 'react';
import { Power, AlertTriangle, TrendingUp, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { autotraderApi, accountApi } from '../../lib/api';
import type { AutotraderStatus, AutotraderLogEntry } from '../../types';
import { PnLDashboard } from './PnLDashboard';
import { ShadowLog } from './ShadowLog';

const CONFIRM_PHRASE = 'I UNDERSTAND THIS TRADES REAL MONEY';

// Editable risk limits. Stored server-side as decimals (0.06); shown and
// typed here as percents (6). The server enforces the hard bounds too.
const RISK_FIELDS = [
  { key: 'max_open_risk_pct',  label: 'Max total open risk', unit: '% of balance', pct: true,  hint: 'Across all open auto positions combined' },
  { key: 'risk_per_trade',     label: 'Risk per trade',      unit: '% of balance', pct: true,  hint: 'Sizing budget for one position' },
  { key: 'daily_loss_cap_pct', label: 'Daily loss cap',      unit: '% of balance', pct: true,  hint: 'Stops new entries once today’s closed losses reach this' },
  { key: 'min_entry_score',    label: 'Min entry score',     unit: 'points',       pct: false, hint: 'Setups scoring below this are skipped' },
  { key: 'profit_lock_activate_pct', label: 'Profit lock starts at', unit: '% gain at the peak', pct: false, hint: 'Once a position has been up this much, its stops are raised so a winner can’t turn into a loser' },
  { key: 'profit_lock_keep_pct',     label: 'Profit lock keeps',     unit: '% of the open gain', pct: false, hint: 'Stops are raised to lock this share of the peak gain (never below breakeven)' },
  { key: 'profit_stall_minutes',     label: 'Close if stalled for',  unit: 'minutes',            pct: false, hint: 'Closes the position if it has been up past the start level but made no new high for this long' },
  { key: 'live_max_contracts', label: 'Live max contracts per trade', unit: 'contracts', pct: false, hint: 'Hard cap in live mode only; a trade splits into two lots, so 2 is the minimum' },
  { key: 'rolling_loss_cap_pct', label: 'Rolling 5-day loss limit', unit: '% of balance', pct: true, hint: 'Switches Auto-Trader OFF, until you turn it back on, once closed losses over 5 trading days reach this' },
] as const;

const DECISION_CLS: Record<string, string> = {
  entered: 'text-bull',
  exited:  'text-sky',
  skipped: 'text-ink-3',
  paused:  'text-amber',
};

/** "Sep 18 9:46:12 AM" in the viewer's local zone. SQLite's datetime('now')
 * is UTC with no zone marker, so it must be parsed as UTC explicitly —
 * a bare parse treats it as local time and shows the wrong hour. */
function formatLogTime(createdAt: string): string {
  const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(createdAt);
  const d = new Date(hasZone ? createdAt : createdAt.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return createdAt;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
}

export function AutoTraderPanel() {
  const [status, setStatus]   = useState<AutotraderStatus | null>(null);
  const [log, setLog]         = useState<AutotraderLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, l] = await Promise.all([autotraderApi.status(), autotraderApi.log(50)]);
      setStatus(s);
      setLog(l);
    } catch {
      setError('Failed to load Auto-Trader status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Risk-limit editor — loaded once, kept out of the 15s status poll so
  // it never overwrites what's being typed.
  const [draft, setDraft]                 = useState<Record<string, string>>({});
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  useEffect(() => {
    accountApi.settings.get()
      .then(s => {
        const d: Record<string, string> = {};
        for (const f of RISK_FIELDS) {
          const v = parseFloat(s[f.key]?.value);
          d[f.key] = isNaN(v) ? '' : String(f.pct ? +(v * 100).toFixed(2) : v);
        }
        setDraft(d);
      })
      .catch(() => setSettingsError('Could not load risk limits'));
  }, []);

  async function saveRiskLimits() {
    setSaving(true);
    setSaved(false);
    setSettingsError(null);
    try {
      const payload: Record<string, string> = {};
      for (const f of RISK_FIELDS) {
        const n = parseFloat(draft[f.key]);
        if (isNaN(n)) throw new Error(`${f.label} must be a number`);
        payload[f.key] = String(f.pct ? +(n / 100).toFixed(6) : n);
      }
      await accountApi.settings.update(payload);
      setSaved(true);
      refresh();
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'Failed to save risk limits');
    } finally {
      setSaving(false);
    }
  }

  // Poll rather than relying solely on WS — this panel needs to be
  // trustworthy even if a WS reconnect is mid-flight.
  useEffect(() => {
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  }, [refresh]);

  async function toggle() {
    if (!status) return;
    try {
      const result = status.enabled ? await autotraderApi.disable() : await autotraderApi.enable();
      setStatus(s => s ? { ...s, enabled: result.enabled } : s);
    } catch {
      setError('Failed to toggle Auto-Trader');
    }
  }

  async function switchToPaper() {
    try {
      await autotraderApi.setMode('paper');
      refresh();
    } catch {
      setError('Failed to switch to paper mode');
    }
  }

  async function confirmSwitchToLive() {
    try {
      await autotraderApi.setMode('live', confirmInput);
      setShowLiveConfirm(false);
      setConfirmInput('');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch to live mode');
    }
  }

  if (!status) {
    return (
      <div className="flex items-center justify-center h-full text-ink-3 text-sm">
        {loading ? 'Loading Auto-Trader status…' : (error ?? 'Loading…')}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">

      {/* Mode banner — unmissable */}
      <div className={clsx(
        'rounded-lg p-3 sm:p-4 border-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3',
        status.mode === 'live' ? 'bg-bear/10 border-bear text-bear' : 'bg-sky/10 border-sky text-sky'
      )}>
        <div>
          <div className="font-display text-xl font-extrabold tracking-tight">
            {status.mode === 'live' ? '🔴 LIVE — REAL MONEY' : '🧪 PAPER TRADING'}
          </div>
          <div className="text-xs opacity-80 mt-0.5">
            {status.enabled ? 'Auto-Trader is ACTIVE — placing entries automatically' : 'Auto-Trader is OFF — no new entries'}
          </div>
        </div>
        <button
          onClick={toggle}
          className={clsx(
            'flex items-center justify-center gap-2 px-4 py-3 sm:py-2 rounded-md font-mono text-sm font-medium border-2 transition-colors',
            status.enabled
              ? 'bg-bear/10 border-bear text-bear hover:bg-bear/20'
              : 'bg-bull/10 border-bull text-bull hover:bg-bull/20'
          )}
        >
          <Power size={14} />
          {status.enabled ? 'Turn Off' : 'Turn On'}
        </button>
      </div>

      {/* Mode switch */}
      <div className="card flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="text-2xs text-ink-3">
          Trading mode — switching to live requires typing a confirmation phrase
        </div>
        <div className="flex gap-2">
          <button onClick={switchToPaper} disabled={status.mode === 'paper'}
            className={clsx('text-2xs font-mono px-2 py-1 rounded border',
              status.mode === 'paper' ? 'bg-sky/10 border-sky/40 text-sky' : 'border-border text-ink-3 hover:text-ink-1')}>
            Paper
          </button>
          <button onClick={() => setShowLiveConfirm(true)} disabled={status.mode === 'live'}
            className={clsx('text-2xs font-mono px-2 py-1 rounded border',
              status.mode === 'live' ? 'bg-bear/10 border-bear/40 text-bear' : 'border-border text-ink-3 hover:text-bear')}>
            Live
          </button>
        </div>
      </div>

      {showLiveConfirm && (
        <div className="card border-bear/40 bg-bear/5 space-y-2">
          <div className="flex items-center gap-1.5 text-bear text-xs font-medium">
            <AlertTriangle size={14} />
            This switches Auto-Trader to real money
          </div>
          <div className="text-2xs text-ink-2">
            Type exactly: <span className="font-mono text-ink-1">{CONFIRM_PHRASE}</span>
          </div>
          <input
            className="input text-xs py-1.5 w-full"
            value={confirmInput}
            onChange={e => setConfirmInput(e.target.value)}
            placeholder="Type the confirmation phrase…"
          />
          <div className="flex gap-2">
            <button
              onClick={confirmSwitchToLive}
              disabled={confirmInput !== CONFIRM_PHRASE}
              className="btn-primary disabled:opacity-40 text-xs px-3 py-1.5"
            >
              Confirm — switch to LIVE
            </button>
            <button onClick={() => { setShowLiveConfirm(false); setConfirmInput(''); }} className="btn-ghost text-xs px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Circuit breaker status */}
      <div className="card grid grid-cols-2 sm:grid-cols-4 gap-3 text-2xs font-mono">
        <div>
          <div className="text-ink-3">Today's P&L</div>
          <div className={status.todayPnl >= 0 ? 'text-bull text-base' : 'text-bear text-base'}>
            {status.todayPnl >= 0 ? '+' : ''}${status.todayPnl.toFixed(2)}
          </div>
        </div>
        <div>
          <div className="text-ink-3">Daily loss cap</div>
          <div className="text-ink-1 text-base">{(status.dailyCapPct * 100).toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-ink-3">Consecutive losses</div>
          <div className={clsx('text-base', status.consecutiveLosses >= 3 ? 'text-bear' : 'text-ink-1')}>
            {status.consecutiveLosses} / 3
          </div>
        </div>
        <div>
          <div className="text-ink-3">Open risk</div>
          <div className={clsx('text-base', status.balance > 0 && status.openRisk >= status.balance * status.maxOpenRiskPct ? 'text-amber' : 'text-ink-1')}>
            ${status.openRisk.toFixed(0)}
            <span className="text-ink-3 text-2xs"> / ${(status.balance * status.maxOpenRiskPct).toFixed(0)}</span>
          </div>
        </div>
        {status.rolling && (
          <div>
            <div className="text-ink-3">5-day P&amp;L vs loss limit</div>
            <div className={clsx('text-base',
              status.rolling.tripped ? 'text-bear'
              : status.rolling.pnl < 0 && -status.rolling.pnl >= status.rolling.capDollars * 0.5 ? 'text-amber' : 'text-ink-1')}>
              {status.rolling.pnl < 0 ? '-' : '+'}${Math.abs(status.rolling.pnl).toFixed(0)}
              <span className="text-ink-3 text-2xs"> / -${status.rolling.capDollars.toFixed(0)}</span>
              {status.rolling.tripped && <span className="text-bear text-2xs"> TRIPPED — off until re-enabled</span>}
            </div>
          </div>
        )}
      </div>

      {/* P&L dashboard — historical performance, separate from the
          circuit-breaker snapshot above (today only) */}
      <PnLDashboard />
      <ShadowLog />

      {/* Risk limits */}
      <div className="card space-y-3">
        <div className="section-label mb-0">Risk Limits</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {RISK_FIELDS.map(f => {
            const n = parseFloat(draft[f.key] ?? '');
            const dollars = f.pct && !isNaN(n) && status.balance > 0 ? (status.balance * n) / 100 : null;
            return (
              <label key={f.key} className="block">
                <span className="text-2xs text-ink-2 font-mono">{f.label}</span>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min="0"
                    className="input text-sm py-2"
                    value={draft[f.key] ?? ''}
                    onChange={e => { setDraft(d => ({ ...d, [f.key]: e.target.value })); setSaved(false); }}
                  />
                  <span className="text-2xs text-ink-3 font-mono whitespace-nowrap">{f.unit === 'points' ? 'pts' : '%'}</span>
                </div>
                <span className="block text-2xs text-ink-3 mt-1">
                  {f.hint}{dollars !== null ? ` · ≈ $${dollars.toFixed(0)}` : ''}
                </span>
              </label>
            );
          })}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={saveRiskLimits} disabled={saving} className="btn-primary px-4 py-2 disabled:opacity-40">
            {saving ? 'Saving…' : 'Save limits'}
          </button>
          {saved && <span className="text-2xs text-bull font-mono">Saved — applies from the next entry</span>}
        </div>
        {settingsError && <div className="text-2xs text-bear">{settingsError}</div>}
      </div>

      {/* Open positions */}
      <div className="card">
        <div className="section-label mb-2 flex items-center gap-1.5">
          <TrendingUp size={11} />
          Open Auto-Positions ({status.openPositions.length})
        </div>
        {status.openPositions.length === 0 ? (
          <div className="text-2xs text-ink-3">No open auto-trader positions</div>
        ) : (
          <div className="space-y-2">
            {status.openPositions.map(p => (
              <div key={p.id} className="text-2xs font-mono border-b border-border/50 pb-1.5 last:border-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-0.5">
                  <div>
                    <span className="text-ink-1 font-medium">{p.sym}</span>
                    <span className="text-ink-3 ml-2">{p.contract_description}</span>
                  </div>
                  <div className="text-ink-2">
                    {p.contracts}x @ ${p.entry_option_price.toFixed(2)}
                    {p.target1_filled ? <span className="text-bull ml-2">trailing (peak ${p.peak_price?.toFixed(2) ?? '—'})</span> : null}
                  </div>
                </div>
                {p.lots && p.lots.length > 0 && (
                  <div className="mt-1 pl-3 space-y-0.5 text-ink-3">
                    {p.lots.map(l => (
                      <div key={l.lot}>
                        Lot {l.lot} · {l.contracts}x ·{' '}
                        {l.status === 'pending' ? 'waiting to fill'
                          : l.status === 'cancelled' ? 'cancelled (never filled)'
                          : l.status === 'closed'
                            ? <span className={(l.pnl_dollars ?? 0) >= 0 ? 'text-bull' : 'text-bear'}>
                                {l.exit_reason} @ ${(l.exit_price ?? 0).toFixed(2)} ({(l.pnl_dollars ?? 0) >= 0 ? '+' : ''}${(l.pnl_dollars ?? 0).toFixed(2)})
                              </span>
                            : l.lot === 'A' ? 'open — target + stop'
                            : `open — runner, stop $${(l.stop_price ?? 0).toFixed(2)}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Decision log */}
      <div className="card">
        <div className="section-label mb-2 flex items-center justify-between">
          <span>Decision Log</span>
          <button onClick={refresh}><RefreshCw size={11} className={clsx('text-ink-3', loading && 'animate-spin')} /></button>
        </div>
        {error && <div className="text-2xs text-bear mb-2">{error}</div>}
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {log.map(entry => (
            <div key={entry.id} className="text-2xs font-mono flex flex-wrap items-start gap-x-2 gap-y-0.5 py-1.5 border-b border-border/30 last:border-0">
              <span className="text-ink-3 flex-shrink-0">{formatLogTime(entry.created_at)}</span>
              <span className="text-ink-1 flex-shrink-0">{entry.sym}</span>
              <span className={clsx('flex-shrink-0 uppercase', DECISION_CLS[entry.decision] ?? 'text-ink-3')}>{entry.decision}</span>
              <span className="text-ink-3 basis-full sm:basis-auto sm:flex-1 min-w-0 break-words">{entry.reason}</span>
            </div>
          ))}
          {log.length === 0 && <div className="text-2xs text-ink-3">No decisions logged yet</div>}
        </div>
      </div>
    </div>
  );
}
